/**
 * Asset Management Hub — nested views with /assets/* URL sync.
 * Uses "Asset Directory" terminology (never "AD").
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HubPage, HubPanel, HubTabs } from '../ui/HubShell';
import { apiService } from '../../services/api';
import { ASSET_PATH_TO_VIEW, ASSET_VIEW_TO_PATH } from '../../config/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
type ViewId =
  | 'dashboard'
  | 'register'
  | 'mapping'
  | 'enrollment-control'
  | 'enrollment-attempts'
  | 'detail';

function viewFromPath(): { view: ViewId; assetId?: string } {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const detail = path.match(/^\/assets\/([0-9a-fA-F-]{36})$/);
  if (detail) return { view: 'detail', assetId: detail[1] };
  const mapped = ASSET_PATH_TO_VIEW[path];
  if (mapped) return { view: mapped as ViewId };
  return { view: 'dashboard' };
}

function pushAssetPath(view: ViewId, assetId?: string) {
  let path = ASSET_VIEW_TO_PATH[view] || '/assets/dashboard';
  if (view === 'detail' && assetId) path = `/assets/${assetId}`;
  if (window.location.pathname !== path) {
    window.history.pushState({ assetView: view, assetId }, '', path);
  }
}

const TABS: { id: ViewId; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'register', label: 'Asset Register' },
  { id: 'mapping', label: 'Device Mapping' },
  { id: 'enrollment-control', label: 'Enrollment Control' },
  { id: 'enrollment-attempts', label: 'Enrollment Attempts' },
];

function parseMoney(raw: string): number {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function roundMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

const DEFAULT_REPAIR_TYPES = [
  'Hardware replacement',
  'Hardware repair',
  'Battery replacement',
  'Screen / display',
  'Keyboard / input',
  'Power / charging',
  'OS / software',
  'Cleaning / preventive',
  'Warranty service',
  'Other',
];
const EMPTY_ASSIGN = {
  assigned_user_id: '',
  assigned_person_name: '',
  assigned_employee_id: '',
  site_branch: '',
  location_name: '',
  assignment_reason: 'New user assignment',
  assignment_reason_detail: '',
  remarks: '',
};

const AssetManagementHub: React.FC = () => {
  const { can } = useAuth();
  const toast = useToast();
  const initial = viewFromPath();
  const [view, setView] = useState<ViewId>(initial.view);
  const [assetId, setAssetId] = useState<string | undefined>(initial.assetId);
  const [dash, setDash] = useState<any>(null);
  const [assets, setAssets] = useState<any[]>([]);
  const [policy, setPolicy] = useState<any>(null);
  const [attempts, setAttempts] = useState<any[]>([]);
  const [matches, setMatches] = useState<any[]>([]);
  const [detail, setDetail] = useState<any>(null);
  const [batch, setBatch] = useState<any>(null);
  const [meta, setMeta] = useState<any>(null);
  const [tenantUsers, setTenantUsers] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [createForm, setCreateForm] = useState({
    serial_number: '',
    asset_hostname: '',
    manufacturer: '',
    model: '',
    site_branch: '',
    location_name: '',
    status: 'In Stock',
    status_detail: '',
  });
  const [assignForm, setAssignForm] = useState({ ...EMPTY_ASSIGN });
  const [statusForm, setStatusForm] = useState({ status: 'In Stock', status_detail: '', remarks: '' });
  const [registerAction, setRegisterAction] = useState<null | { type: 'status' | 'reassign' | 'location'; asset: any }>(null);
  const [rowStatusForm, setRowStatusForm] = useState({ status: 'In Stock', status_detail: '', remarks: '' });
  const [rowAssignForm, setRowAssignForm] = useState({ ...EMPTY_ASSIGN });
  const [rowLocationForm, setRowLocationForm] = useState({ site_branch: '', location_name: '' });
  const [repairForm, setRepairForm] = useState({
    remarks: '',
    vendor: '',
    repair_type: 'Hardware repair',
    repair_type_detail: '',
    spares_cost: '',
    service_cost: '',
    gst_percent: '',
    gst_amount: '',
    performed_on: '',
    set_under_repair: false,
  });
  const [warrantyForm, setWarrantyForm] = useState({
    warranty_start_date: '',
    warranty_end_date: '',
    vendor: '',
    invoice_no: '',
    remarks: '',
  });

  const statuses: { value: string; label: string }[] = meta?.statuses || [
    { value: 'In Stock', label: 'In Stock' },
    { value: 'Assigned', label: 'Assigned' },
    { value: 'In Use', label: 'In Use' },
    { value: 'In Transit', label: 'In Transit' },
    { value: 'Under Repair', label: 'Under Repair' },
    { value: 'Returned', label: 'Returned' },
    { value: 'Lost', label: 'Missing / Lost' },
    { value: 'Retired', label: 'Retired' },
    { value: 'Scrapped', label: 'Scrapped' },
    { value: 'Disposed', label: 'Disposed' },
    { value: 'Stolen', label: 'Stolen' },
    { value: 'Other', label: 'Other' },
  ];
  const assignmentReasons: string[] = meta?.assignment_reasons || [
    'New user assignment',
    'Repaired and reassigning',
    'Role / department change',
    'Temporary loan',
    'Return to stock',
    'Other',
  ];
  const repairTypes: string[] = meta?.repair_types || DEFAULT_REPAIR_TYPES;
  const statusChoicesWithoutAssigned = statuses.filter((s) => s.value !== 'Assigned');
  const repairSpares = parseMoney(repairForm.spares_cost);
  const repairService = parseMoney(repairForm.service_cost);
  const repairGstPct = repairForm.gst_percent.trim() === '' ? null : Number(repairForm.gst_percent);
  const repairGstTyped = repairForm.gst_amount.trim() !== '';
  const repairGstAmount = repairGstTyped
    ? parseMoney(repairForm.gst_amount)
    : (repairGstPct != null && Number.isFinite(repairGstPct)
      ? roundMoney((repairSpares + repairService) * repairGstPct / 100)
      : 0);
  const repairTotal = roundMoney(repairSpares + repairService + repairGstAmount);

  const go = useCallback((next: ViewId, id?: string) => {
    setView(next);
    setAssetId(id);
    pushAssetPath(next, id);
  }, []);

  useEffect(() => {
    const onPop = () => {
      const v = viewFromPath();
      setView(v.view);
      setAssetId(v.assetId);
    };
    window.addEventListener('popstate', onPop);
    if (!window.location.pathname.startsWith('/assets')) {
      pushAssetPath(view, assetId);
    }
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    apiService.getAssetsMeta().then(setMeta).catch(() => setMeta(null));
    apiService.tenantListUsers().then(setTenantUsers).catch(() => setTenantUsers([]));
  }, []);

  const reload = useCallback(async () => {
    setError(null);
    try {
      if (view === 'dashboard') setDash(await apiService.getAssetsDashboard());
      if (view === 'register' || view === 'dashboard') setAssets(await apiService.listAssets({ limit: 200 }));
      if (view === 'enrollment-control') setPolicy(await apiService.getAssetEnrollmentPolicy());
      if (view === 'enrollment-attempts') setAttempts(await apiService.listAssetEnrollmentAttempts());
      if (view === 'mapping') setMatches(await apiService.getAssetDeviceMatches());
      if (view === 'detail' && assetId) {
        const d = await apiService.getAsset(assetId);
        setDetail(d);
        setAssignForm({
          ...EMPTY_ASSIGN,
          assigned_user_id: d.assigned_user_id || '',
          assigned_person_name: d.assigned_person_name || '',
          assigned_employee_id: d.assigned_employee_id || '',
          site_branch: d.site_branch || '',
          location_name: d.location_name || '',
        });
        setStatusForm({
          status: d.status === 'Assigned' ? 'In Use' : d.status || 'In Stock',
          status_detail: d.status_detail || '',
          remarks: '',
        });
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    }
  }, [view, assetId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const cards = useMemo(() => {
    if (!dash) return [];
    return [
      ['Total Assets', dash.total_assets],
      ['Linked Assets', dash.linked_assets],
      ['Unlinked Assets', dash.unlinked_assets],
      ['Assigned Assets', dash.assigned_assets],
      ['Unassigned Assets', dash.unassigned_assets],
      ['Warranty Expiring', dash.warranty_expiring],
      ['Retired/Scrapped', dash.retired_scrapped],
      ['Enrollment Blocked', dash.enrollment_blocked],
    ];
  }, [dash]);

  return (
    <HubPage
      title="Asset Directory"
      subtitle="Company asset register and enrollment validation"
    >
      <HubTabs
        items={TABS.map((t) => ({ id: t.id, label: t.label }))}
        value={(view === 'detail' ? 'register' : view) as ViewId}
        onChange={(id) => go(id as ViewId)}
      />
      {error && (
        <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
      )}

      {view === 'dashboard' && (
        <HubPanel>
          <h2 className="text-lg font-semibold mb-3">Asset Dashboard</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {cards.map(([label, value]) => (
              <div key={String(label)} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-2xl font-semibold text-slate-900">{value ?? 0}</div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            {(
              [
                ['By Category', dash?.by_category],
                ['By Status', dash?.by_status],
                ['By Location', dash?.by_location],
                ['By OS', dash?.by_os],
              ] as const
            ).map(([title, rows]) => (
              <div key={title} className="rounded border border-slate-200 p-3">
                <div className="font-medium mb-2">{title}</div>
                <ul className="space-y-1">
                  {(rows || []).slice(0, 8).map((r: any) => (
                    <li key={r.name} className="flex justify-between text-slate-600">
                      <span>{r.name}</span>
                      <span>{r.count}</span>
                    </li>
                  ))}
                  {!rows?.length && <li className="text-slate-400">No data</li>}
                </ul>
              </div>
            ))}
          </div>
        </HubPanel>
      )}

      {view === 'register' && (
        <HubPanel>
          <h2 className="text-lg font-semibold mb-3">Asset Register</h2>

          {(can('assets.all.import') || can('assets.all.create')) && (
            <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 space-y-3">
              <div>
                <p className="font-medium text-slate-900">Bulk upload</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  Download the Excel template, fill rows, then upload here. Serial Number is required; Asset Code is auto-generated. Missing manufacturer/model/OS fills from the enrolled device later.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="inline-flex items-center rounded bg-indigo-600 text-white px-3 py-2 text-sm"
                  onClick={async () => {
                    try {
                      const blob = await apiService.downloadAssetImportTemplate();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'asset_import_template.xlsx';
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (err: any) {
                      setError(err?.message || 'Template download failed');
                    }
                  }}
                >
                  Download Excel template
                </button>
                {can('assets.all.import') && (
                  <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <span className="rounded border border-slate-300 bg-white px-3 py-2 hover:bg-slate-100">
                      {busy ? 'Uploading…' : 'Choose file (.xlsx / .csv)'}
                    </span>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xlsm"
                      className="sr-only"
                      disabled={busy}
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (!f) return;
                        setBusy(true);
                        setError(null);
                        try {
                          const res = await apiService.uploadAssetImport(f);
                          const preview = await apiService.getAssetImportBatch(res.batch_id);
                          setBatch(preview);
                          toast.success(
                            `${preview.valid_rows ?? 0} valid row(s) ready to confirm`,
                            'Import uploaded',
                          );
                        } catch (err: any) {
                          const msg = err?.message || 'Upload failed';
                          setError(msg);
                          toast.error(msg);
                        } finally {
                          setBusy(false);
                        }
                      }}
                    />
                  </label>
                )}
              </div>
              {meta?.template_columns?.length ? (
                <details>
                  <summary className="cursor-pointer text-indigo-700 text-xs">Template columns</summary>
                  <p className="mt-1 text-xs text-slate-600">{meta.template_columns.join(' · ')}</p>
                </details>
              ) : null}
              {batch && (
                <div className="rounded border border-slate-200 bg-white p-3">
                  <div className="text-sm mb-2">
                    Batch {batch.batch_id}: {batch.valid_rows} valid / {batch.error_rows} errors /{' '}
                    {batch.matched_rows} matched devices · status {batch.status}
                  </div>
                  {batch.status !== 'Confirmed' && can('assets.all.import') && (
                    <button
                      type="button"
                      className="rounded bg-slate-800 text-white px-3 py-2 text-sm mb-2"
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        try {
                          await apiService.confirmAssetImport(batch.batch_id);
                          setBatch(await apiService.getAssetImportBatch(batch.batch_id));
                          await reload();
                          toast.success('Import confirmed and assets saved');
                        } catch (err: any) {
                          const msg = err?.message || 'Confirm failed';
                          setError(msg);
                          toast.error(msg);
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      Confirm Import
                    </button>
                  )}
                  <div className="overflow-auto max-h-56">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="text-left border-b">
                          <th className="py-1 pr-2">Row</th>
                          <th className="py-1 pr-2">Action</th>
                          <th className="py-1 pr-2">Match</th>
                          <th className="py-1 pr-2">Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(batch.rows || []).map((r: any) => (
                          <tr key={r.row_number} className="border-b border-slate-50">
                            <td className="py-1 pr-2">{r.row_number}</td>
                            <td className="py-1 pr-2">{r.action}</td>
                            <td className="py-1 pr-2">{r.match_status || '—'}</td>
                            <td className="py-1 pr-2 text-red-600">{(r.validation_errors || []).join('; ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {can('assets.all.create') && (
            <>
              <h3 className="text-sm font-medium text-slate-800 mb-2">Add single asset</h3>
            <form
              className="mb-4 grid md:grid-cols-4 gap-2 items-end"
              onSubmit={async (e) => {
                e.preventDefault();
                if (createForm.status === 'Other' && !createForm.status_detail.trim()) {
                  setError('Status Detail is required when Status is Other');
                  return;
                }
                setBusy(true);
                try {
                  await apiService.createAsset({
                    ...createForm,
                    status_detail: createForm.status === 'Other' ? createForm.status_detail : undefined,
                  });
                  setCreateForm({
                    serial_number: '',
                    asset_hostname: '',
                    manufacturer: '',
                    model: '',
                    site_branch: '',
                    location_name: '',
                    status: 'In Stock',
                    status_detail: '',
                  });
                  await reload();
                  toast.success('Asset created');
                } catch (err: any) {
                  const msg = err?.message || 'Create failed';
                  setError(msg);
                  toast.error(msg);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <label className="text-xs">
                Serial Number*
                <input className="mt-1 w-full border rounded px-2 py-1" value={createForm.serial_number} onChange={(e) => setCreateForm({ ...createForm, serial_number: e.target.value })} required />
              </label>
              <label className="text-xs">
                Asset Hostname
                <input className="mt-1 w-full border rounded px-2 py-1" placeholder="defaults to serial" value={createForm.asset_hostname} onChange={(e) => setCreateForm({ ...createForm, asset_hostname: e.target.value })} />
              </label>
              <label className="text-xs">
                Site / Branch
                <input className="mt-1 w-full border rounded px-2 py-1" value={createForm.site_branch} onChange={(e) => setCreateForm({ ...createForm, site_branch: e.target.value })} />
              </label>
              <label className="text-xs">
                Location
                <input className="mt-1 w-full border rounded px-2 py-1" value={createForm.location_name} onChange={(e) => setCreateForm({ ...createForm, location_name: e.target.value })} />
              </label>
              <label className="text-xs">
                Manufacturer
                <input className="mt-1 w-full border rounded px-2 py-1" value={createForm.manufacturer} onChange={(e) => setCreateForm({ ...createForm, manufacturer: e.target.value })} />
              </label>
              <label className="text-xs">
                Model
                <input className="mt-1 w-full border rounded px-2 py-1" value={createForm.model} onChange={(e) => setCreateForm({ ...createForm, model: e.target.value })} />
              </label>
              <label className="text-xs">
                Status
                <select className="mt-1 w-full border rounded px-2 py-1" value={createForm.status} onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}>
                  {statuses.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </label>
              {createForm.status === 'Other' && (
                <label className="text-xs">
                  Status Detail*
                  <input className="mt-1 w-full border rounded px-2 py-1" value={createForm.status_detail} onChange={(e) => setCreateForm({ ...createForm, status_detail: e.target.value })} required />
                </label>
              )}
              <button type="submit" disabled={busy} className="rounded bg-slate-800 text-white px-3 py-2 text-sm disabled:opacity-50">
                Add Asset
              </button>
            </form>
            </>
          )}
          {registerAction && (
            <div className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 text-sm">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-slate-800">
                  {registerAction.type === 'status'
                    ? 'Change status'
                    : registerAction.type === 'location'
                      ? 'Edit Site / Location'
                      : 'Assign asset'}{' '}
                  — {registerAction.asset.asset_hostname || registerAction.asset.serial_number}
                </h3>
                <button type="button" className="text-slate-500 underline text-xs" onClick={() => setRegisterAction(null)}>Close</button>
              </div>
              {registerAction.type === 'status' ? (
                <form
                  className="grid md:grid-cols-4 gap-2 items-end"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (rowStatusForm.status === 'Assigned') {
                      setRegisterAction({ type: 'reassign', asset: registerAction.asset });
                      setRowAssignForm({
                        ...EMPTY_ASSIGN,
                        assigned_person_name: registerAction.asset.assigned_person_name || '',
                        assigned_employee_id: registerAction.asset.assigned_employee_id || '',
                        site_branch: registerAction.asset.site_branch || '',
                        location_name: registerAction.asset.location_name || '',
                        assigned_user_id: registerAction.asset.assigned_user_id || '',
                      });
                      return;
                    }
                    if (rowStatusForm.status === 'Other' && !rowStatusForm.status_detail.trim()) {
                      setError('Status Detail is required when Status is Other');
                      return;
                    }
                    setBusy(true);
                    try {
                      await apiService.changeAssetStatus(registerAction.asset.id, rowStatusForm.status, {
                        status_detail: rowStatusForm.status_detail,
                        remarks: rowStatusForm.remarks,
                      });
                      setRegisterAction(null);
                      await reload();
                      toast.success('Status updated');
                    } catch (err: any) {
                      const msg = err?.message || 'Status update failed';
                      setError(msg);
                      toast.error(msg);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <label className="text-xs">
                    Status
                    <select
                      className="mt-1 w-full border rounded px-2 py-1"
                      value={rowStatusForm.status}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === 'Assigned') {
                          setRegisterAction({ type: 'reassign', asset: registerAction.asset });
                          setRowAssignForm({
                            ...EMPTY_ASSIGN,
                            assigned_person_name: registerAction.asset.assigned_person_name || '',
                            assigned_employee_id: registerAction.asset.assigned_employee_id || '',
                            site_branch: registerAction.asset.site_branch || '',
                            location_name: registerAction.asset.location_name || '',
                            assigned_user_id: registerAction.asset.assigned_user_id || '',
                          });
                          return;
                        }
                        setRowStatusForm({ ...rowStatusForm, status: v });
                      }}
                    >
                      {statusChoicesWithoutAssigned.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </label>
                  <p className="text-xs text-slate-500 md:col-span-3">
                    To set status to <strong>Assigned</strong>, use the <strong>Assign</strong> action (requires person, Employee ID, Site, Location, Remarks).
                  </p>
                  {rowStatusForm.status === 'Other' && (
                    <label className="text-xs">
                      Status Detail*
                      <input className="mt-1 w-full border rounded px-2 py-1" value={rowStatusForm.status_detail} onChange={(e) => setRowStatusForm({ ...rowStatusForm, status_detail: e.target.value })} required />
                    </label>
                  )}
                  <label className="text-xs">
                    Remarks
                    <input className="mt-1 w-full border rounded px-2 py-1" value={rowStatusForm.remarks} onChange={(e) => setRowStatusForm({ ...rowStatusForm, remarks: e.target.value })} />
                  </label>
                  <button type="submit" disabled={busy} className="rounded bg-slate-800 text-white px-3 py-2 text-sm">Save status</button>
                </form>
              ) : registerAction.type === 'location' ? (
                <form
                  className="grid md:grid-cols-3 gap-2 items-end"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!rowLocationForm.site_branch.trim() || !rowLocationForm.location_name.trim()) {
                      setError('Site / Branch and Location are required');
                      return;
                    }
                    setBusy(true);
                    try {
                      await apiService.updateAsset(registerAction.asset.id, {
                        site_branch: rowLocationForm.site_branch.trim(),
                        location_name: rowLocationForm.location_name.trim(),
                      });
                      setRegisterAction(null);
                      await reload();
                      toast.success('Site / Location saved');
                    } catch (err: any) {
                      const msg = err?.message || 'Location update failed';
                      setError(msg);
                      toast.error(msg);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <label className="text-xs">
                    Site / Branch*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={rowLocationForm.site_branch} onChange={(e) => setRowLocationForm({ ...rowLocationForm, site_branch: e.target.value })} required />
                  </label>
                  <label className="text-xs">
                    Location*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={rowLocationForm.location_name} onChange={(e) => setRowLocationForm({ ...rowLocationForm, location_name: e.target.value })} required />
                  </label>
                  <button type="submit" disabled={busy} className="rounded bg-slate-800 text-white px-3 py-2 text-sm">Save location</button>
                </form>
              ) : (
                <form
                  className="grid md:grid-cols-3 gap-2 items-end"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const returning = rowAssignForm.assignment_reason === 'Return to stock';
                    if (!rowAssignForm.assignment_reason) {
                      setError('Assignment Reason is required');
                      return;
                    }
                    if (rowAssignForm.assignment_reason === 'Other' && !rowAssignForm.assignment_reason_detail.trim()) {
                      setError('Assignment Reason Detail is required when reason is Other');
                      return;
                    }
                    if (!returning) {
                      if (!rowAssignForm.assigned_person_name.trim()) {
                        setError('Assigned to (person name) is required');
                        return;
                      }
                      if (!rowAssignForm.assigned_employee_id.trim()) {
                        setError('Employee ID is required');
                        return;
                      }
                      if (!rowAssignForm.site_branch.trim() || !rowAssignForm.location_name.trim()) {
                        setError('Site / Branch and Location are required');
                        return;
                      }
                      if (!rowAssignForm.remarks.trim()) {
                        setError('Remarks are required when assigning');
                        return;
                      }
                    }
                    setBusy(true);
                    try {
                      await apiService.assignAsset(registerAction.asset.id, {
                        assigned_user_id: rowAssignForm.assigned_user_id || null,
                        assigned_person_name: rowAssignForm.assigned_person_name.trim() || undefined,
                        assigned_employee_id: rowAssignForm.assigned_employee_id.trim() || undefined,
                        site_branch: rowAssignForm.site_branch.trim(),
                        location_name: rowAssignForm.location_name.trim(),
                        assignment_reason: rowAssignForm.assignment_reason,
                        assignment_reason_detail: rowAssignForm.assignment_reason_detail,
                        remarks: rowAssignForm.remarks.trim(),
                      });
                      setRegisterAction(null);
                      await reload();
                      toast.success(
                        returning ? 'Asset returned to stock' : 'Asset assigned',
                      );
                    } catch (err: any) {
                      const msg = err?.message || 'Assign failed';
                      setError(msg);
                      toast.error(msg);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <label className="text-xs">
                    Assigned to (person name)*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={rowAssignForm.assigned_person_name} onChange={(e) => setRowAssignForm({ ...rowAssignForm, assigned_person_name: e.target.value })} required={rowAssignForm.assignment_reason !== 'Return to stock'} />
                  </label>
                  <label className="text-xs">
                    Employee ID*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={rowAssignForm.assigned_employee_id} onChange={(e) => setRowAssignForm({ ...rowAssignForm, assigned_employee_id: e.target.value })} required={rowAssignForm.assignment_reason !== 'Return to stock'} />
                  </label>
                  <label className="text-xs">
                    Hub user (optional)
                    <select className="mt-1 w-full border rounded px-2 py-1" value={rowAssignForm.assigned_user_id} onChange={(e) => setRowAssignForm({ ...rowAssignForm, assigned_user_id: e.target.value })}>
                      <option value="">— None —</option>
                      {tenantUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.full_name || u.username}{u.email ? ` (${u.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs">
                    Site / Branch*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={rowAssignForm.site_branch} onChange={(e) => setRowAssignForm({ ...rowAssignForm, site_branch: e.target.value })} required={rowAssignForm.assignment_reason !== 'Return to stock'} />
                  </label>
                  <label className="text-xs">
                    Location*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={rowAssignForm.location_name} onChange={(e) => setRowAssignForm({ ...rowAssignForm, location_name: e.target.value })} required={rowAssignForm.assignment_reason !== 'Return to stock'} />
                  </label>
                  <label className="text-xs">
                    Assignment Reason*
                    <select className="mt-1 w-full border rounded px-2 py-1" value={rowAssignForm.assignment_reason} onChange={(e) => setRowAssignForm({ ...rowAssignForm, assignment_reason: e.target.value })} required>
                      {assignmentReasons.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </label>
                  {rowAssignForm.assignment_reason === 'Other' && (
                    <label className="text-xs md:col-span-2">
                      Assignment Reason Detail*
                      <input className="mt-1 w-full border rounded px-2 py-1" value={rowAssignForm.assignment_reason_detail} onChange={(e) => setRowAssignForm({ ...rowAssignForm, assignment_reason_detail: e.target.value })} required />
                    </label>
                  )}
                  <label className="text-xs md:col-span-2">
                    Remarks*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={rowAssignForm.remarks} onChange={(e) => setRowAssignForm({ ...rowAssignForm, remarks: e.target.value })} required={rowAssignForm.assignment_reason !== 'Return to stock'} />
                  </label>
                  <button type="submit" disabled={busy} className="rounded bg-slate-800 text-white px-3 py-2 text-sm">Save assignment</button>
                </form>
              )}
            </div>
          )}
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-3">Asset Code</th>
                  <th className="py-2 pr-3">Asset Hostname</th>
                  <th className="py-2 pr-3">Serial Number</th>
                  <th className="py-2 pr-3">Site / Branch</th>
                  <th className="py-2 pr-3">Location</th>
                  <th className="py-2 pr-3">Assigned To</th>
                  <th className="py-2 pr-3">Employee ID</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3">Device Enrolled</th>
                  <th className="py-2 pr-3">Device Hostname</th>
                  <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-mono text-xs">{a.asset_code || '—'}</td>
                    <td className="py-2 pr-3">{a.asset_hostname}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{a.serial_number}</td>
                    <td className="py-2 pr-3">{a.site_branch || '—'}</td>
                    <td className="py-2 pr-3">{a.location_name || '—'}</td>
                    <td className="py-2 pr-3">{a.assigned_person_name || '—'}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{a.assigned_employee_id || '—'}</td>
                    <td className="py-2 pr-3">
                      {a.status_label || a.status}
                      {a.status === 'Other' && a.status_detail ? ` (${a.status_detail})` : ''}
                    </td>
                    <td className="py-2 pr-3" title={a.source || ''}>
                      {a.source_label ||
                        (String(a.source || '').toLowerCase() === 'direct_enroll'
                          ? 'Directly enrolled'
                          : 'Asset directory registered')}
                    </td>
                    <td className="py-2 pr-3">
                      {a.device_enrolled || a.linked_device_id ? (
                        <span className="text-emerald-700 font-medium">Yes</span>
                      ) : (
                        <span className="text-slate-400">No</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">{a.device_hostname || '—'}</td>
                    <td className="py-2 pr-3 space-x-2 whitespace-nowrap">
                      <button className="text-blue-700 underline" onClick={() => go('detail', a.id)}>Open</button>
                      {can('assets.all.edit') && (
                        <button
                          className="text-blue-700 underline"
                          onClick={() => {
                            setRegisterAction({ type: 'location', asset: a });
                            setRowLocationForm({
                              site_branch: a.site_branch || '',
                              location_name: a.location_name || '',
                            });
                          }}
                        >
                          Location
                        </button>
                      )}
                      {can('assets.all.edit') && (
                        <button
                          className="text-blue-700 underline"
                          onClick={() => {
                            setRegisterAction({ type: 'status', asset: a });
                            setRowStatusForm({
                              status: a.status === 'Assigned' ? 'In Use' : a.status || 'In Stock',
                              status_detail: a.status_detail || '',
                              remarks: '',
                            });
                          }}
                        >
                          Status
                        </button>
                      )}
                      {can('assets.all.assign') && (
                        <button
                          className="text-blue-700 underline"
                          onClick={() => {
                            setRegisterAction({ type: 'reassign', asset: a });
                            setRowAssignForm({
                              ...EMPTY_ASSIGN,
                              assigned_user_id: a.assigned_user_id || '',
                              assigned_person_name: a.assigned_person_name || '',
                              assigned_employee_id: a.assigned_employee_id || '',
                              site_branch: a.site_branch || '',
                              location_name: a.location_name || '',
                            });
                          }}
                        >
                          Assign
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {!assets.length && (
                  <tr>
                    <td colSpan={12} className="py-6 text-center text-slate-400">No assets yet. Upload Excel above or add one.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </HubPanel>
      )}

      {view === 'detail' && detail && (
        <HubPanel>
          <h2 className="text-lg font-semibold mb-3">Asset · {detail.asset_hostname || detail.serial_number}</h2>
          <button className="text-sm text-blue-700 underline mb-3" onClick={() => go('register')}>← Back to register</button>
          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <div className="space-y-1">
              <div><span className="text-slate-500">Asset Hostname:</span> {detail.asset_hostname}</div>
              <div><span className="text-slate-500">Device Hostname:</span> {detail.device_hostname || '—'}</div>
              <div><span className="text-slate-500">Serial:</span> {detail.serial_number}</div>
              <div><span className="text-slate-500">Manufacturer:</span> {detail.manufacturer || '—'}</div>
              <div><span className="text-slate-500">Model:</span> {detail.model || '—'}</div>
              <div><span className="text-slate-500">Chassis:</span> {[detail.chassis_type, detail.chassis_vendor].filter(Boolean).join(' · ') || '—'}</div>
              <div><span className="text-slate-500">OS:</span> {detail.os_name || '—'}</div>
              <div><span className="text-slate-500">Processor:</span> {detail.processor || '—'}</div>
              <div><span className="text-slate-500">RAM / Storage:</span> {detail.ram_gb != null ? `${detail.ram_gb} GB` : '—'} / {detail.storage_gb != null ? `${detail.storage_gb} GB` : '—'}</div>
              <div><span className="text-slate-500">Warranty:</span> {detail.warranty_end_date ? `until ${detail.warranty_end_date}` : '—'}{detail.vendor ? ` · ${detail.vendor}` : ''}</div>
              <div><span className="text-slate-500">Site / Branch:</span> {detail.site_branch || '—'}</div>
              <div><span className="text-slate-500">Location:</span> {detail.location_name || '—'}</div>
              <div>
                <span className="text-slate-500">Status:</span> {detail.status_label || detail.status}
                {detail.status === 'Other' && detail.status_detail ? ` — ${detail.status_detail}` : ''}
              </div>
              <div>
                <span className="text-slate-500">Source:</span>{' '}
                <span title={detail.source || ''}>
                  {detail.source_label ||
                    (String(detail.source || '').toLowerCase() === 'direct_enroll'
                      ? 'Directly enrolled'
                      : 'Asset directory registered')}
                </span>
              </div>
              <div><span className="text-slate-500">Assigned To:</span> {detail.assigned_person_name || '—'}</div>
              <div><span className="text-slate-500">Employee ID:</span> {detail.assigned_employee_id || '—'}</div>
              <div>
                <span className="text-slate-500">Device Enrolled:</span>{' '}
                {detail.device_enrolled ? 'Yes' : 'No'}
                {detail.device_enrolled && (
                  <span className="text-slate-500"> — blank hardware fields are filled from the enrolled device (admin values are kept).</span>
                )}
              </div>
              <div><span className="text-slate-500">Enrollment:</span> {detail.enrollment_status}</div>
            </div>

            {can('assets.all.edit') && (
              <form
                className="space-y-2 border rounded-lg p-3 bg-slate-50"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (statusForm.status === 'Other' && !statusForm.status_detail.trim()) {
                    setError('Status Detail is required when Status is Other');
                    return;
                  }
                  setBusy(true);
                  try {
                    await apiService.changeAssetStatus(detail.id, statusForm.status, {
                      status_detail: statusForm.status_detail,
                      remarks: statusForm.remarks,
                    });
                    await reload();
                    toast.success('Status updated');
                  } catch (err: any) {
                    const msg = err?.message || 'Status update failed';
                    setError(msg);
                    toast.error(msg);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <h3 className="font-medium">Change status</h3>
                <p className="text-xs text-slate-500">Use Assign below to set status to Assigned.</p>
                <label className="block text-xs">
                  Status
                  <select className="mt-1 w-full border rounded px-2 py-1" value={statusForm.status} onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}>
                    {statusChoicesWithoutAssigned.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </label>
                {statusForm.status === 'Other' && (
                  <label className="block text-xs">
                    Status Detail*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={statusForm.status_detail} onChange={(e) => setStatusForm({ ...statusForm, status_detail: e.target.value })} required />
                  </label>
                )}
                <label className="block text-xs">
                  Remarks
                  <input className="mt-1 w-full border rounded px-2 py-1" value={statusForm.remarks} onChange={(e) => setStatusForm({ ...statusForm, remarks: e.target.value })} />
                </label>
                <button type="submit" className="rounded bg-slate-800 text-white px-3 py-1.5 text-sm" disabled={busy}>Update status</button>
              </form>
            )}

            {can('assets.all.edit') && (
              <form
                className="space-y-2 border rounded-lg p-3 bg-slate-50"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!assignForm.site_branch.trim() || !assignForm.location_name.trim()) {
                    setError('Site / Branch and Location are required');
                    return;
                  }
                  setBusy(true);
                  try {
                    await apiService.updateAsset(detail.id, {
                      site_branch: assignForm.site_branch.trim(),
                      location_name: assignForm.location_name.trim(),
                    });
                    await reload();
                    toast.success('Site / Location saved');
                  } catch (err: any) {
                    const msg = err?.message || 'Location update failed';
                    setError(msg);
                    toast.error(msg);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <h3 className="font-medium">Edit Site / Location</h3>
                <label className="block text-xs">
                  Site / Branch*
                  <input className="mt-1 w-full border rounded px-2 py-1" value={assignForm.site_branch} onChange={(e) => setAssignForm({ ...assignForm, site_branch: e.target.value })} required />
                </label>
                <label className="block text-xs">
                  Location*
                  <input className="mt-1 w-full border rounded px-2 py-1" value={assignForm.location_name} onChange={(e) => setAssignForm({ ...assignForm, location_name: e.target.value })} required />
                </label>
                <button type="submit" className="rounded bg-slate-800 text-white px-3 py-1.5 text-sm" disabled={busy}>Save location</button>
              </form>
            )}

            {can('assets.all.assign') && (
              <form
                className="space-y-2 border rounded-lg p-3 bg-slate-50 md:col-span-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const returning = assignForm.assignment_reason === 'Return to stock';
                  if (assignForm.assignment_reason === 'Other' && !assignForm.assignment_reason_detail.trim()) {
                    setError('Assignment Reason Detail is required when reason is Other');
                    return;
                  }
                  if (!returning) {
                    if (!assignForm.assigned_person_name.trim() || !assignForm.assigned_employee_id.trim()) {
                      setError('Assigned to and Employee ID are required');
                      return;
                    }
                    if (!assignForm.site_branch.trim() || !assignForm.location_name.trim()) {
                      setError('Site / Branch and Location are required');
                      return;
                    }
                    if (!assignForm.remarks.trim()) {
                      setError('Remarks are required when assigning');
                      return;
                    }
                  }
                  setBusy(true);
                  try {
                    await apiService.assignAsset(detail.id, {
                      assigned_user_id: assignForm.assigned_user_id || null,
                      assigned_person_name: assignForm.assigned_person_name.trim() || undefined,
                      assigned_employee_id: assignForm.assigned_employee_id.trim() || undefined,
                      site_branch: assignForm.site_branch.trim(),
                      location_name: assignForm.location_name.trim(),
                      assignment_reason: assignForm.assignment_reason,
                      assignment_reason_detail: assignForm.assignment_reason_detail,
                      remarks: assignForm.remarks.trim(),
                    });
                    await reload();
                    toast.success(
                      returning ? 'Asset returned to stock' : 'Asset assigned',
                    );
                  } catch (err: any) {
                    const msg = err?.message || 'Assignment failed';
                    setError(msg);
                    toast.error(msg);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <h3 className="font-medium">Assign asset</h3>
                <div className="grid md:grid-cols-3 gap-2">
                  <label className="text-xs">
                    Assigned to (person name)*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={assignForm.assigned_person_name} onChange={(e) => setAssignForm({ ...assignForm, assigned_person_name: e.target.value })} required={assignForm.assignment_reason !== 'Return to stock'} />
                  </label>
                  <label className="text-xs">
                    Employee ID*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={assignForm.assigned_employee_id} onChange={(e) => setAssignForm({ ...assignForm, assigned_employee_id: e.target.value })} required={assignForm.assignment_reason !== 'Return to stock'} />
                  </label>
                  <label className="text-xs">
                    Hub user (optional)
                    <select className="mt-1 w-full border rounded px-2 py-1" value={assignForm.assigned_user_id} onChange={(e) => setAssignForm({ ...assignForm, assigned_user_id: e.target.value })}>
                      <option value="">— None —</option>
                      {tenantUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.full_name || u.username}{u.email ? ` (${u.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs">
                    Site / Branch*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={assignForm.site_branch} onChange={(e) => setAssignForm({ ...assignForm, site_branch: e.target.value })} required={assignForm.assignment_reason !== 'Return to stock'} />
                  </label>
                  <label className="text-xs">
                    Location*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={assignForm.location_name} onChange={(e) => setAssignForm({ ...assignForm, location_name: e.target.value })} required={assignForm.assignment_reason !== 'Return to stock'} />
                  </label>
                  <label className="text-xs">
                    Reason*
                    <select className="mt-1 w-full border rounded px-2 py-1" value={assignForm.assignment_reason} onChange={(e) => setAssignForm({ ...assignForm, assignment_reason: e.target.value })} required>
                      {assignmentReasons.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </label>
                  {assignForm.assignment_reason === 'Other' && (
                    <label className="text-xs md:col-span-2">
                      Reason detail*
                      <input className="mt-1 w-full border rounded px-2 py-1" value={assignForm.assignment_reason_detail} onChange={(e) => setAssignForm({ ...assignForm, assignment_reason_detail: e.target.value })} required />
                    </label>
                  )}
                  <label className="text-xs md:col-span-3">
                    Remarks*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={assignForm.remarks} onChange={(e) => setAssignForm({ ...assignForm, remarks: e.target.value })} required={assignForm.assignment_reason !== 'Return to stock'} />
                  </label>
                </div>
                <button type="submit" className="rounded bg-slate-800 text-white px-3 py-1.5 text-sm" disabled={busy}>Save assignment</button>
              </form>
            )}

            {can('assets.all.edit') && (
              <form
                className="space-y-2 border rounded-lg p-3 bg-slate-50"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!repairForm.repair_type) {
                    setError('Repair type is required');
                    return;
                  }
                  if (repairForm.repair_type === 'Other' && !repairForm.repair_type_detail.trim()) {
                    setError('Repair type detail is required when type is Other');
                    return;
                  }
                  if (!repairForm.remarks.trim()) {
                    setError('Repair remarks are required');
                    return;
                  }
                  setBusy(true);
                  try {
                    await apiService.logAssetRepair(detail.id, {
                      remarks: repairForm.remarks,
                      vendor: repairForm.vendor || undefined,
                      repair_type: repairForm.repair_type,
                      repair_type_detail: repairForm.repair_type_detail.trim() || undefined,
                      spares_cost: repairSpares,
                      service_cost: repairService,
                      gst_percent: repairGstPct != null && Number.isFinite(repairGstPct) ? repairGstPct : null,
                      gst_amount: repairGstAmount,
                      performed_on: repairForm.performed_on || undefined,
                      set_under_repair: repairForm.set_under_repair,
                    });
                    setRepairForm({
                      remarks: '',
                      vendor: '',
                      repair_type: 'Hardware repair',
                      repair_type_detail: '',
                      spares_cost: '',
                      service_cost: '',
                      gst_percent: '',
                      gst_amount: '',
                      performed_on: '',
                      set_under_repair: false,
                    });
                    await reload();
                    toast.success('Repair logged');
                  } catch (err: any) {
                    const msg = err?.message || 'Failed to log repair';
                    setError(msg);
                    toast.error(msg);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <h3 className="font-medium">Log repair</h3>
                <label className="block text-xs">
                  Repair type*
                  <select
                    className="mt-1 w-full border rounded px-2 py-1"
                    value={repairForm.repair_type}
                    onChange={(e) => setRepairForm({ ...repairForm, repair_type: e.target.value })}
                    required
                  >
                    {repairTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </label>
                {repairForm.repair_type === 'Other' && (
                  <label className="block text-xs">
                    Repair type detail*
                    <input className="mt-1 w-full border rounded px-2 py-1" value={repairForm.repair_type_detail} onChange={(e) => setRepairForm({ ...repairForm, repair_type_detail: e.target.value })} required />
                  </label>
                )}
                <label className="block text-xs">
                  Remarks*
                  <input className="mt-1 w-full border rounded px-2 py-1" value={repairForm.remarks} onChange={(e) => setRepairForm({ ...repairForm, remarks: e.target.value })} required />
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs">
                    Vendor
                    <input className="mt-1 w-full border rounded px-2 py-1" value={repairForm.vendor} onChange={(e) => setRepairForm({ ...repairForm, vendor: e.target.value })} />
                  </label>
                  <label className="block text-xs">
                    Performed on
                    <input type="date" className="mt-1 w-full border rounded px-2 py-1" value={repairForm.performed_on} onChange={(e) => setRepairForm({ ...repairForm, performed_on: e.target.value })} />
                  </label>
                  <label className="block text-xs">
                    Spares cost (INR)
                    <input type="number" min="0" step="0.01" className="mt-1 w-full border rounded px-2 py-1" value={repairForm.spares_cost} onChange={(e) => setRepairForm({ ...repairForm, spares_cost: e.target.value })} />
                  </label>
                  <label className="block text-xs">
                    Service cost (INR)
                    <input type="number" min="0" step="0.01" className="mt-1 w-full border rounded px-2 py-1" value={repairForm.service_cost} onChange={(e) => setRepairForm({ ...repairForm, service_cost: e.target.value })} />
                  </label>
                  <label className="block text-xs">
                    GST %
                    <input type="number" min="0" max="100" step="0.01" className="mt-1 w-full border rounded px-2 py-1" value={repairForm.gst_percent} onChange={(e) => setRepairForm({ ...repairForm, gst_percent: e.target.value, gst_amount: '' })} placeholder="optional" />
                  </label>
                  <label className="block text-xs">
                    GST amount (INR)
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="mt-1 w-full border rounded px-2 py-1"
                      value={repairGstTyped ? repairForm.gst_amount : (repairGstAmount ? String(repairGstAmount) : '')}
                      onChange={(e) => setRepairForm({ ...repairForm, gst_amount: e.target.value })}
                      placeholder={repairGstPct != null ? 'auto from %' : 'optional'}
                    />
                  </label>
                  <div className="text-xs col-span-2 rounded border border-slate-200 bg-white px-2 py-1.5">
                    Total: <span className="font-medium">INR {repairTotal.toFixed(2)}</span>
                    <span className="text-slate-500"> (spares + service + GST)</span>
                  </div>
                  <label className="flex items-end gap-2 text-xs pb-1 col-span-2">
                    <input type="checkbox" checked={repairForm.set_under_repair} onChange={(e) => setRepairForm({ ...repairForm, set_under_repair: e.target.checked })} />
                    Set status to Under Repair
                  </label>
                </div>
                <button type="submit" className="rounded bg-slate-800 text-white px-3 py-1.5 text-sm" disabled={busy}>Save repair</button>
              </form>
            )}

            {can('assets.all.edit') && (
              <form
                className="space-y-2 border rounded-lg p-3 bg-slate-50"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!warrantyForm.warranty_end_date) {
                    setError('Warranty end date is required');
                    return;
                  }
                  setBusy(true);
                  try {
                    await apiService.renewAssetWarranty(detail.id, {
                      warranty_end_date: warrantyForm.warranty_end_date,
                      warranty_start_date: warrantyForm.warranty_start_date || undefined,
                      vendor: warrantyForm.vendor || undefined,
                      invoice_no: warrantyForm.invoice_no || undefined,
                      remarks: warrantyForm.remarks || undefined,
                    });
                    setWarrantyForm({ warranty_start_date: '', warranty_end_date: '', vendor: '', invoice_no: '', remarks: '' });
                    await reload();
                    toast.success('Warranty renewed');
                  } catch (err: any) {
                    const msg = err?.message || 'Warranty renewal failed';
                    setError(msg);
                    toast.error(msg);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <h3 className="font-medium">Warranty renewal</h3>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs">
                    Start date
                    <input type="date" className="mt-1 w-full border rounded px-2 py-1" value={warrantyForm.warranty_start_date} onChange={(e) => setWarrantyForm({ ...warrantyForm, warranty_start_date: e.target.value })} />
                  </label>
                  <label className="block text-xs">
                    End date*
                    <input type="date" className="mt-1 w-full border rounded px-2 py-1" value={warrantyForm.warranty_end_date} onChange={(e) => setWarrantyForm({ ...warrantyForm, warranty_end_date: e.target.value })} required />
                  </label>
                  <label className="block text-xs">
                    Vendor
                    <input className="mt-1 w-full border rounded px-2 py-1" value={warrantyForm.vendor} onChange={(e) => setWarrantyForm({ ...warrantyForm, vendor: e.target.value })} />
                  </label>
                  <label className="block text-xs">
                    Invoice / PO
                    <input className="mt-1 w-full border rounded px-2 py-1" value={warrantyForm.invoice_no} onChange={(e) => setWarrantyForm({ ...warrantyForm, invoice_no: e.target.value })} />
                  </label>
                  <label className="block text-xs col-span-2">
                    Remarks
                    <input className="mt-1 w-full border rounded px-2 py-1" value={warrantyForm.remarks} onChange={(e) => setWarrantyForm({ ...warrantyForm, remarks: e.target.value })} />
                  </label>
                </div>
                <button type="submit" className="rounded bg-slate-800 text-white px-3 py-1.5 text-sm" disabled={busy}>Save warranty</button>
              </form>
            )}

            <div className="md:col-span-2 border rounded-lg p-3">
              <div className="font-medium mb-2">Asset history</div>
              <p className="text-xs text-slate-500 mb-2">Assignments, status changes, enrollment hardware pulls, repairs, and warranty renewals.</p>
              <ul className="space-y-2 max-h-80 overflow-auto">
                {(detail.lifecycle || []).map((e: any) => {
                  const label =
                    e.event_type === 'device_linked' ? 'Device linked'
                    : e.event_type === 'hardware_enriched' ? 'Hardware filled from enrolled device'
                    : e.event_type === 'repair_logged' ? 'Repair'
                    : e.event_type === 'warranty_renewed' ? 'Warranty renewed'
                    : e.event_type === 'assigned' ? 'Assigned'
                    : e.event_type === 'unassigned' ? 'Unassigned'
                    : e.event_type === 'status_changed' ? 'Status changed'
                    : e.event_type === 'created' ? 'Created'
                    : e.event_type;
                  const filled = e.event_details?.filled ? Object.keys(e.event_details.filled).join(', ') : '';
                  const conflicts = e.event_details?.conflicts ? Object.keys(e.event_details.conflicts).join(', ') : '';
                  return (
                    <li key={e.id || `${e.event_type}-${e.created_at}`} className="border-b border-slate-100 pb-2 text-slate-700">
                      <div className="text-xs text-slate-400">{e.created_at || '—'}</div>
                      <div className="font-medium text-slate-800">{label}</div>
                      {(e.old_status || e.new_status) && (
                        <div className="text-xs">
                          {e.old_status || '—'} → {e.new_status_label || e.new_status || '—'}
                        </div>
                      )}
                      {e.remarks && <div className="text-xs">{e.remarks}</div>}
                      {filled && <div className="text-xs text-emerald-700">Filled: {filled}</div>}
                      {conflicts && <div className="text-xs text-amber-700">Kept admin values (device differed): {conflicts}</div>}
                      {e.event_type === 'warranty_renewed' && e.event_details?.warranty_end_date && (
                        <div className="text-xs">New end: {e.event_details.warranty_end_date}</div>
                      )}
                      {e.event_type === 'repair_logged' && (
                        <div className="text-xs">
                          {e.event_details?.repair_type
                            ? `Type: ${e.event_details.repair_type}${e.event_details.repair_type_detail ? ` (${e.event_details.repair_type_detail})` : ''}`
                            : null}
                          {e.event_details?.vendor ? ` · Vendor: ${e.event_details.vendor}` : ''}
                          {e.event_details?.spares_cost != null || e.event_details?.service_cost != null || e.event_details?.gst_amount != null ? (
                            <div>
                              Spares {e.event_details.spares_cost ?? 0}
                              {' · '}Service {e.event_details.service_cost ?? 0}
                              {' · '}GST {e.event_details.gst_amount ?? 0}
                              {e.event_details.gst_percent != null ? ` (${e.event_details.gst_percent}%)` : ''}
                              {' · '}Total {e.event_details.cost ?? 0}
                            </div>
                          ) : e.event_details?.cost != null ? (
                            <div>Total {e.event_details.cost}</div>
                          ) : null}
                        </div>
                      )}
                    </li>
                  );
                })}
                {!detail.lifecycle?.length && <li className="text-slate-400">No history yet</li>}
              </ul>
            </div>
          </div>
        </HubPanel>
      )}

      {view === 'mapping' && (
        <HubPanel>
          <h2 className="text-lg font-semibold mb-3">Device Mapping</h2>
          <p className="text-sm text-slate-600 mb-3">
            Suggested exact serial matches between unlinked assets and enrolled devices.
          </p>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b text-slate-500">
                <th className="py-2 pr-3">Asset Hostname</th>
                <th className="py-2 pr-3">Serial</th>
                <th className="py-2 pr-3">Device Hostname</th>
                <th className="py-2 pr-3">Confidence</th>
                <th className="py-2 pr-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => (
                <tr key={`${m.asset_id}-${m.device_id}`} className="border-b">
                  <td className="py-2 pr-3">{m.asset_hostname}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{m.serial_number}</td>
                  <td className="py-2 pr-3">{m.device_hostname}</td>
                  <td className="py-2 pr-3">{m.match_confidence}</td>
                  <td className="py-2 pr-3">
                    {can('assets.all.link_device') && (
                      <button
                        className="text-blue-700 underline"
                        onClick={async () => {
                          try {
                            await apiService.linkAssetDevice(m.asset_id, m.device_id);
                            await reload();
                            toast.success('Device linked to asset');
                          } catch (err: any) {
                            const msg = err?.message || 'Link failed';
                            setError(msg);
                            toast.error(msg);
                          }
                        }}
                      >
                        Link
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!matches.length && (
                <tr>
                  <td colSpan={5} className="py-6 text-slate-400">
                    No suggested matches.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </HubPanel>
      )}

      {view === 'enrollment-control' && policy && (
        <HubPanel>
          <h2 className="text-lg font-semibold mb-3">Enrollment Control · Asset Directory</h2>
          <p className="text-sm text-slate-600 mb-3">
            Use Asset Directory for Enrollment Validation. Strict mode can block new enrollments if
            assets are not preloaded — run Audit Only first.
          </p>
          {policy.mode === 'REQUIRE_ASSET' || policy.use_asset_directory ? (
            <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Strict mode can block new device enrollment if assets are not preloaded. Run Audit Only
              first.
            </div>
          ) : null}
          <form
            className="grid md:grid-cols-2 gap-3 text-sm max-w-3xl"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                const updated = await apiService.updateAssetEnrollmentPolicy(policy);
                setPolicy(updated);
                toast.success('Enrollment policy saved');
              } catch (err: any) {
                const msg = err?.message || 'Save failed';
                setError(msg);
                toast.error(msg);
              } finally {
                setBusy(false);
              }
            }}
          >
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!policy.use_asset_directory}
                onChange={(e) => setPolicy({ ...policy, use_asset_directory: e.target.checked })}
                disabled={!can('assets.enrollment.policy_edit')}
              />
              Use Asset Directory for Enrollment Validation
            </label>
            <label>
              Enrollment Mode
              <select
                className="mt-1 w-full border rounded px-2 py-1"
                value={policy.mode}
                onChange={(e) => setPolicy({ ...policy, mode: e.target.value })}
                disabled={!can('assets.enrollment.policy_edit')}
              >
                <option value="OFF">OFF</option>
                <option value="AUDIT_ONLY">AUDIT_ONLY</option>
                <option value="REQUIRE_ASSET_OR_APPROVAL">REQUIRE_ASSET_OR_APPROVAL</option>
                <option value="REQUIRE_ASSET">REQUIRE_ASSET</option>
              </select>
            </label>
            {(
              [
                ['require_serial_number', 'Require Serial Number'],
                ['allow_missing_serial', 'Allow Missing Serial'],
                ['allow_mac_fallback', 'Allow MAC Fallback'],
                ['allow_hostname_fallback', 'Allow Hostname Fallback'],
                ['allow_manual_approval', 'Allow Manual Approval'],
                ['block_retired_assets', 'Block Retired Assets'],
                ['block_lost_assets', 'Block Lost Assets'],
                ['block_stolen_assets', 'Block Stolen Assets'],
                ['block_scrapped_assets', 'Block Scrapped Assets'],
                ['block_disposed_assets', 'Block Disposed Assets'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!policy[key]}
                  onChange={(e) => setPolicy({ ...policy, [key]: e.target.checked })}
                  disabled={!can('assets.enrollment.policy_edit')}
                />
                {label}
              </label>
            ))}
            {can('assets.enrollment.policy_edit') && (
              <button type="submit" className="rounded bg-slate-800 text-white px-3 py-2 md:col-span-2" disabled={busy}>
                Save Asset Directory Policy
              </button>
            )}
          </form>
        </HubPanel>
      )}

      {view === 'enrollment-attempts' && (
        <HubPanel>
          <h2 className="text-lg font-semibold mb-3">Enrollment Attempts</h2>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b text-slate-500">
                <th className="py-2 pr-3">Time</th>
                <th className="py-2 pr-3">Serial</th>
                <th className="py-2 pr-3">Device Hostname</th>
                <th className="py-2 pr-3">Decision</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((a) => (
                <tr key={a.id} className="border-b">
                  <td className="py-2 pr-3 whitespace-nowrap text-xs">{a.created_at}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{a.attempted_serial_number || '—'}</td>
                  <td className="py-2 pr-3">{a.device_hostname || '—'}</td>
                  <td className="py-2 pr-3 text-xs">{a.decision}</td>
                  <td className="py-2 pr-3">{a.status}</td>
                  <td className="py-2 pr-3 space-x-2">
                    {a.status === 'Pending Approval' && can('assets.enrollment.attempts_approve') && (
                      <>
                        <button
                          className="text-green-700 underline"
                          onClick={async () => {
                            try {
                              await apiService.approveAssetEnrollmentAttempt(a.id);
                              await reload();
                              toast.success('Enrollment approved');
                            } catch (err: any) {
                              const msg = err?.message || 'Approve failed';
                              setError(msg);
                              toast.error(msg);
                            }
                          }}
                        >
                          Approve
                        </button>
                        <button
                          className="text-blue-700 underline"
                          onClick={async () => {
                            try {
                              await apiService.createAssetAndApproveAttempt(a.id, {
                                serial_number: a.attempted_serial_number,
                                asset_hostname: a.attempted_serial_number,
                                manufacturer: a.manufacturer,
                                model: a.model,
                              });
                              await reload();
                              toast.success('Asset created and enrollment approved');
                            } catch (err: any) {
                              const msg = err?.message || 'Create & approve failed';
                              setError(msg);
                              toast.error(msg);
                            }
                          }}
                        >
                          Create Asset & Approve
                        </button>
                      </>
                    )}
                    {a.status === 'Pending Approval' && can('assets.enrollment.attempts_reject') && (
                      <button
                        className="text-red-700 underline"
                        onClick={async () => {
                          try {
                            await apiService.rejectAssetEnrollmentAttempt(a.id);
                            await reload();
                            toast.success('Enrollment rejected');
                          } catch (err: any) {
                            const msg = err?.message || 'Reject failed';
                            setError(msg);
                            toast.error(msg);
                          }
                        }}
                      >
                        Reject
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!attempts.length && (
                <tr>
                  <td colSpan={6} className="py-6 text-slate-400">
                    No enrollment attempts recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </HubPanel>
      )}
    </HubPage>
  );
};

export default AssetManagementHub;
